import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import { withTranslation } from "react-i18next";
import "bootstrap/dist/css/bootstrap.min.css";

// components
import Breadcrumbs from "components/Common/Breadcrumb";
import DataTable from "../../components/Common/LocalizedDataTable";
// APIs
import { UserApis } from "apis/UserApi";
import config from "config/config";
import { RoleApi } from "apis/RoleApi";
import { formatNumber } from "helpers/language_helper";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const AllAdminUsers = (props) => {
  const query = useQuery();
  const history = useHistory();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeData, setActiveData] = useState({
    page: 1,
    totalRows: 0,
    search: "",
    limit: config.LIMIT,
  });

  useEffect(() => {
    fetchUsers(activeData);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchUsers({ ...activeData });
    // eslint-disable-next-line
  }, [query]);

  const columns = [
    {
      name: props.t("Sr.No"),
      selector: (row) => row.sr_no,
      cell: (row) => formatNumber(row.sr_no),
      sortable: false,
    },
    { name: props.t("User ID"), selector: (row) => row.user_id, sortable: true },
    { name: props.t("Full Name"), selector: (row) => row.full_name, sortable: true },
    { name: props.t("Email"), selector: (row) => row.email, sortable: true },
    { name: props.t("Role"), selector: (row) => row.role, sortable: true },
    { name: props.t("Status"), selector: (row) => row.status, sortable: true },
    { name: props.t("Action"), selector: (row) => row.action },
  ];

  const fetchUsers = (dataParams) => {
    setLoading(true);

    UserApis.getAdminUsers({
      page: dataParams.page,
      limit: dataParams.limit,
      search: dataParams.search,
    })
      .then((res) => {
        const apiData = res?.data?.data || [];
        const pagination = res?.data?.pagination || {};

        // Map API data to table rows
        const users = apiData.map((v, i) => ({
          sr_no: (pagination.current_page - 1) * pagination.per_page + (i + 1),
          user_id: v.id,
          full_name: [v.firstname, v.middlename, v.lastname].filter(Boolean).join(" "),
          email: v.email || "-",
          phone: v.phone || "-",
          role: v.role_name || "-",
          status: (
            <span
              style={{
                fontSize: "13px",
                fontWeight: "500",
                color: v.status === 1 ? "#28a745" : "#dc3545",
              }}
            >
              {v.status === 1 ? props.t("Active") : props.t("Inactive")}
            </span>
          ),
          action: (
            <button
              className="btn btn-sm btn-link"
              onClick={() => history.push(`/view-admin-user/${v.id}`)}
            >
              <i className="fas fa-eye" title={props.t("View")} />
            </button>
          ),
        }));

        // Update state
        setData(users);
        setActiveData({
          page: pagination.current_page || dataParams.page,
          totalRows: pagination.total || apiData.length,
          search: dataParams.search,
          limit: pagination.per_page || dataParams.limit,
        });

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error("❌ Error fetching admin users:", err);
      });
  };

  const handlePageChange = (page) => {
    const updatedData = { ...activeData, page };
    setActiveData(updatedData);
    fetchUsers(updatedData);
  };

  const handleRowsPerPageChange = (limit) => {
    const updatedData = { ...activeData, limit, page: 1 }; // reset to page 1
    setActiveData(updatedData);
    fetchUsers(updatedData);
  };

  // Page title
  document.title = props.t("All Admin Users") + " | " + props.t("Salon");

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title={props.t("Users")}
          breadcrumbItem={props.t("All Admin Users")}
        />
      </div>

      <div className="container-fluid">
        <DataTable
          className="table-bordered"
          progressPending={loading}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={activeData.totalRows} // total rows from API
          paginationPerPage={activeData.limit}       // rows per page
          onChangeRowsPerPage={handleRowsPerPageChange}
          onChangePage={handlePageChange}
          noHeader
          highlightOnHover
        />
      </div>
    </div>
  );
};

AllAdminUsers.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(AllAdminUsers);
